import { useLayoutEffect, type RefObject } from 'react';

/**
 * Lines up the boxes of fields that share a row.
 *
 * Forms lay fields out in halves and thirds, and when one field's label wraps
 * to two lines (common in Malayalam, and in narrow modals) its box sits lower
 * than its neighbours'. CSS cannot size a label by the other labels in its row
 * without restructuring every field, so this measures instead: labels that
 * open a field and start on the same line of the same layout get the tallest
 * one's height.
 *
 * Re-runs when the area resizes, when fields come and go, and on `deps`
 * (pass the language, so a switch re-measures).
 */
export function useAlignedLabels(rootRef: RefObject<HTMLElement>, deps: unknown[]) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;
    const align = () => {
      frame = 0;
      const labels = [...root.querySelectorAll<HTMLLabelElement>('label:first-child')].filter(
        // A field's own label: the field holds a box to line up, and the label is not a tick box.
        (l) => !l.querySelector('input[type=checkbox], input[type=radio]') && l.parentElement?.querySelector('input, select, textarea, button'),
      );
      labels.forEach((l) => (l.style.minHeight = ''));
      const rows = new Map<Element, Map<number, HTMLLabelElement[]>>();
      for (const l of labels) {
        const layout = l.parentElement?.parentElement;
        if (!layout || !l.offsetParent) continue;
        const top = Math.round(l.getBoundingClientRect().top);
        const byTop = rows.get(layout) ?? new Map<number, HTMLLabelElement[]>();
        byTop.set(top, [...(byTop.get(top) ?? []), l]);
        rows.set(layout, byTop);
      }
      for (const byTop of rows.values()) {
        for (const row of byTop.values()) {
          if (row.length < 2) continue;
          const tallest = Math.max(...row.map((l) => l.getBoundingClientRect().height));
          row.forEach((l) => (l.style.minHeight = `${tallest}px`));
        }
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(align);
    };
    align();
    const ro = new ResizeObserver(schedule);
    ro.observe(root);
    // Fields shown or hidden (an error line, a conditional field) move rows.
    // childList only: the min-heights set above are attribute changes.
    const mo = new MutationObserver(schedule);
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
